import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

const PaymentReqLog = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Payment Request Log");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const [dateFrom, setDateFrom] = useState(dayjs().startOf('month'));
    const [dateTo, setDateTo] = useState(dayjs().endOf('month'));

    const colDefs = useMemo(() => [
        { field: 'PropName', headerName: 'Property', width: 190 },
        { field: 'TrjDocNo', headerName: 'DocNo', width: 90 },
        {
            field: 'CreatedDate',
            headerName: 'DocDt',
            width: 150,
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                    if (cellValue == null) return -1;

                    const dateParts = cellValue.split(' ')[0].split('-'); // 'DD-MM-YYYY'
                    const cellDate = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);

                    // Reset time from filterDate
                    const filter = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());

                    if (cellDate < filter) return -1;
                    if (cellDate > filter) return 1;
                    return 0;
                },
                browserDatePicker: true,
            }
        },
        {
            field: 'TrjType', headerName: 'TrjType', width: 100,
            cellStyle: params => {
                if (params.value === 'TrnPytAdv') {
                    return { color: 'green', fontWeight: 'bold' }
                }
            }
        },
        { field: 'ResidentName', headerName: 'Resident', width: 140 },
        { field: 'phone', headerName: 'Mobile', width: 120 },
        { field: 'email', headerName: 'Email', width: 190 },
        { field: 'amount', headerName: 'Amount', width: 100 },
        {
            field: 'Gatewaytype', headerName: 'Pyt Gateway', width: 120,
            cellStyle: params => {
                if (params.value === 'Instamojo') {
                    return { color: params.value === 'Instamojo' ? 'purple' : 'green', fontWeight: 'bold' }
                } else if (params.value === 'CashFree') {
                    return { color: params.value === 'CashFree' ? 'green' : 'purple', fontWeight: 'bold' }
                }
            }
        },
        {
            field: 'payment_status', headerName: 'PytStatus', width: 100,
            cellStyle: params => {
                if (params.value === 'SUCCESS') {
                    return { color: 'green', fontWeight: 'bold' }
                } else {
                    return { color: 'red', fontWeight: 'bold' }
                }
            }
        },
        { field: 'purpose', headerName: 'Purpose', width: 100 },
        { field: 'longUrl', headerName: 'PaymentLink', width: 400 },
        { field: 'cf_link_id', headerName: 'LinkId', width: 150 },
        { field: 'cf_payment_id', headerName: 'PaymentId', width: 150 },
        { field: 'Remark', headerName: 'Remark', width: 220 },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
        copyable: true
    }), []);

    useEffect(() => {
        fetchPaymentReq()
    }, [globalProperty]);

    const fetchPaymentReq = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`PaymentGatewayLog/GetPaymentRequestLogDashBoard`, {
                start: 1,
                PageSize: 1,
                SearchText: "",
                PropId: globalProperty,
                SearchFrom: dayjs(dateFrom).format('YYYY-MM-DD'),
                SearchTo: dayjs(dateTo).format('YYYY-MM-DD'),
                Flag: ''
            });

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(pytReq => ({
                    ...pytReq,
                    PaymentLogId: pytReq.PaymentLogId,
                    PropName: pytReq.PropName || '',
                    email: pytReq.email || '',
                    phone: pytReq.phone || '',
                    CreatedDate: pytReq.CreatedDate ? dayjs(pytReq.CreatedDate).format('DD-MM-YYYY HH:mm:ss') : '',
                }));
                setRowData(formattedData);
            } else {
                setRowData([]);
                toast.info(response.data.Data);
            }
        } catch (error) {
            setRowData([]);
            toast.error('Error fetching payment data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    async function handleFetchedData() {
        await fetchPaymentReq();
    };

    const onGridReady = useCallback((params) => {
        // No sizeColumnsToFit here to preserve flex sizing
    }, []);

    const handleRowDoubleClick = () => {

    };

    const handleClearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/payments-log')}
                    >
                        Payment Logs
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Payment Request Log
                    </Typography>
                </Breadcrumbs>

                {/* Buttons container */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 1, md: 0 },
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <DatePicker
                                    label="From"
                                    value={dateFrom}
                                    onChange={(newValue) => setDateFrom(newValue)}
                                    format="DD/MM/YYYY"
                                    views={['day', 'month', 'year']}
                                    sx={{ width: 150 }}
                                />
                                <DatePicker
                                    label="To"
                                    value={dateTo}
                                    onChange={(newValue) => setDateTo(newValue)}
                                    format="DD/MM/YYYY"
                                    views={['day', 'month', 'year']}
                                    sx={{ width: 150 }}
                                />
                            </Box>
                        </LocalizationProvider>
                        <Tooltip title='Retreive data' arrow>
                            <Button variant='contained'
                                onClick={handleFetchedData}
                                sx={{
                                    borderRadius: '20px', backgroundColor: '#635bff', '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                }}
                            >
                                Get Data
                            </Button>
                        </Tooltip>
                    </Box>

                    <Tooltip title='Clear all applied filter' arrow>
                        <Button variant="outlined"
                            onClick={handleClearFilters}
                            size="small"
                            sx={{
                                borderRadius: "50px",
                                textTransform: "none",
                                borderColor: "#635bff",
                                color: "#635bff",
                                "&:hover": {
                                    borderColor: "#524ccc",
                                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                                }
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Tooltip>

                    <Tooltip title='Previous Page' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}
                        >
                            <IconButton
                                onClick={() => navigate('/reports/payments-log')}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 35,
                                    height: 35,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine"
                sx={{ height: 'calc(95% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        onRowDoubleClicked={handleRowDoubleClick}
                        paginationPageSizeSelector={[100, 500, 1000, 5000]}
                        onGridReady={onGridReady}
                        enableCellTextSelection={true}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        tooltipShowDelay={0}
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
                        })}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: 16,
                left: 60,
                fontSize: '14px',
                color: 'black',
            }}>
                Total Records: <b>{rowData.length}</b>
            </Box>
        </Box>
    );
};

export default PaymentReqLog;
